import { useState, useRef, useEffect } from 'react';
import Box from '@mui/material/Box';
import TextField from '@mui/material/TextField';
import SorryModal from './SorryModal';
import Toast from './Toast';
import './Eligibility.css';
import { useCurrentUser } from '../../hooks/useCurrentUser';
import axiosInstance from '../../helpers/axios';

const tfSx = {
  width: 350,
  mb: 2,
  '& label.Mui-focused': { color: '#000' },
  '& .MuiOutlinedInput-root': {
    '& fieldset': { borderColor: '#707C8B' },
    '&:hover fieldset': { borderColor: '#707C8B' },
    '&.Mui-focused fieldset': { borderColor: '#707C8B' },
  },
};

const tfLabelSx = {
  bgcolor: '#fff8f4',
  fontFamily: 'Outfit',
  fontWeight: 'bold',
  borderRadius: '5px',
  fontSize: '14px',
  lineHeight: '20.25px',
  padding: '2px 8px',
  color: '#000',
};

const tfInputProps = { style: { fontFamily: 'Outfit' } };

const PAN_REGEX = /^[A-Za-z]{5}[0-9]{4}[A-Za-z]$/;

const INSTITUTE_AUTOFILL_ROLES = ['institute_user', 'institute_admin'];

export default function RinuxEligibility() {
  const decode = useCurrentUser();
  const role = decode?.role || '';
  const instituteName = decode?.instituteName || '';
  const instituteId = decode?.instituteId || '';
  const isInstituteAutofillRole = INSTITUTE_AUTOFILL_ROLES.includes(role);

  const [form, setForm] = useState({
    mobile: '', name: '', institute: '', course: '', fees: '', studentName: '', pan: '', dob: '',
  });
  const [sameAsStudent, setSameAsStudent] = useState(false);
  const [error, setError] = useState('');

  // Institute search (shown for roles other than institute_user/institute_admin)
  const [instQuery, setInstQuery] = useState('');
  const [instResults, setInstResults] = useState([]);
  const [instOpen, setInstOpen] = useState(false);
  const [selectedInstId, setSelectedInstId] = useState(null);

  // Course search
  const [courseQuery, setCourseQuery] = useState('');
  const [courseResults, setCourseResults] = useState([]);
  const [courseOpen, setCourseOpen] = useState(false);

  // Step navigation: 'form' | 'choose' | 'otp'
  const [step, setStep] = useState('form');

  // OTP step
  const [otpDigits, setOtpDigits] = useState(['', '', '', '', '', '']);
  const [consentChecked, setConsentChecked] = useState(false);
  const otpRefs = useRef([]);

  const [submitting, setSubmitting] = useState(false);
  const [createdApp, setCreatedApp] = useState({ userId: null, applicationId: null });

  // Sorry modal (shown when eligibility-pan returns ruleResult 'red')
  const [showSorry, setShowSorry] = useState(false);

  // Toast
  const [toastMsg, setToastMsg] = useState('');
  const [toastVisible, setToastVisible] = useState(false);
  const [toastType, setToastType] = useState('error');

  const instDebounce = useRef(null);
  const courseDebounce = useRef(null);
  const instDropRef = useRef(null);
  const courseDropRef = useRef(null);

  const handleOtpDigitChange = (index, val) => {
    const digit = val.replace(/\D/g, '').slice(-1);
    const updated = [...otpDigits];
    updated[index] = digit;
    setOtpDigits(updated);
    if (digit && index < 5) otpRefs.current[index + 1]?.focus();
  };

  const handleOtpKeyDown = (index, e) => {
    if (e.key === 'Backspace') {
      if (otpDigits[index]) {
        const updated = [...otpDigits];
        updated[index] = '';
        setOtpDigits(updated);
      } else if (index > 0) {
        otpRefs.current[index - 1]?.focus();
      }
    } else if (e.key === 'ArrowLeft' && index > 0) {
      otpRefs.current[index - 1]?.focus();
    } else if (e.key === 'ArrowRight' && index < 5) {
      otpRefs.current[index + 1]?.focus();
    }
  };

  const handleOtpPaste = (e) => {
    e.preventDefault();
    const pasted = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, 6);
    const updated = Array.from({ length: 6 }, (_, i) => pasted[i] || '');
    setOtpDigits(updated);
    const focusIndex = Math.min(pasted.length, 5);
    otpRefs.current[focusIndex]?.focus();
  };

  // Institute id backing the course search: the logged-in institute user's own
  // institute when auto-filled, otherwise whichever institute was searched/selected.
  const effectiveInstituteId = isInstituteAutofillRole ? instituteId : selectedInstId;

  // ── Effects ────────────────────────────────────────────────────────

  useEffect(() => {
    if (isInstituteAutofillRole && instituteName) setForm(p => ({ ...p, institute: instituteName }));
  }, [isInstituteAutofillRole, instituteName]);

  useEffect(() => {
    const handler = (e) => {
      if (instDropRef.current && !instDropRef.current.contains(e.target)) setInstOpen(false);
      if (courseDropRef.current && !courseDropRef.current.contains(e.target)) setCourseOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  // ── Helpers ────────────────────────────────────────────────────────

  const showToast = (msg, type = 'error') => {
    setToastMsg(msg);
    setToastType(type);
    setToastVisible(true);
  };

  // ── Institute search ───────────────────────────────────────────────

  const handleInstSearch = (query) => {
    setInstQuery(query);
    setForm(p => ({ ...p, institute: '', course: '' }));
    setSelectedInstId(null);
    setCourseQuery('');
    setCourseResults([]);
    if (instDebounce.current) clearTimeout(instDebounce.current);
    if (!query) { setInstResults([]); setInstOpen(false); return; }
    instDebounce.current = setTimeout(async () => {
      try {
        const base = process.env.REACT_APP_BASE_URL;
        const res = await fetch(`${base}/institute/info/name?name=${encodeURIComponent(query)}`, {});
        const json = await res.json();
        const results = json.data || [];
        setInstResults(results);
        setInstOpen(results.length > 0);
      } catch {
        showToast('Failed to search institutes. Please try again.');
      }
    }, 300);
  };

  const selectInst = (opt) => {
    setInstQuery(opt.brandName);
    setForm(p => ({ ...p, institute: opt.brandName, course: '' }));
    setSelectedInstId(opt.id);
    setInstOpen(false);
    setCourseQuery('');
    setCourseResults([]);
  };

  // ── Course search ──────────────────────────────────────────────────

  const handleCourseSearch = (query) => {
    setCourseQuery(query);
    setForm(p => ({ ...p, course: query }));
    if (courseDebounce.current) clearTimeout(courseDebounce.current);
    if (!query || !effectiveInstituteId) { setCourseResults([]); setCourseOpen(false); return; }
    courseDebounce.current = setTimeout(async () => {
      try {
        const base = process.env.REACT_APP_BASE_URL;
        const res = await fetch(
          `${base}/institute/course/search?instituteId=${effectiveInstituteId}&name=${encodeURIComponent(query)}`,
          {}
        );
        const json = await res.json();
        const results = json.data?.courses || [];
        setCourseResults(results);
        setCourseOpen(results.length > 0);
      } catch {
        showToast('Failed to search courses. Please try again.');
      }
    }, 300);
  };

  const selectCourse = (opt) => {
    setCourseQuery(opt.courseName);
    setForm(p => ({ ...p, course: opt.courseName }));
    setCourseOpen(false);
  };

  // ── Submit ─────────────────────────────────────────────────────────
  // API integration points (commented out — wire up during integration phase):
  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const nameParts = form.name.trim().split(/\s+/);
      const firstName = nameParts[0] || '';
      const lastName = nameParts.slice(1).join(' ') || firstName;

      const payload = {
        mobile: form.mobile,
        firstName,
        lastName,
        instituteName: form.institute,
        courseName: form.course,
        courseFees: Number(form.fees) || 0,
        channel: 6,
        studentName: form.studentName,
        pan: form.pan,
        dateOfBirth: form.dob,
      };

      const res = await axiosInstance.post('/summary/eligibility', payload);
      const { userId, applicationId } = res?.data?.data || {};
      setCreatedApp({ userId, applicationId });
      setStep('choose');
    } catch (err) {
      const msg = err?.response?.data?.message || 'Failed to create application. Please try again.';
      showToast(msg);
    } finally {
      setSubmitting(false);
    }
  };

  // ── OTP / Link handlers ────────────────────────────────────────────

  const [otpLoading, setOtpLoading] = useState(false);
  const [verifyLoading, setVerifyLoading] = useState(false);

  const handleSendOtp = async () => {
    setOtpLoading(true);
    try {
      await axiosInstance.post('/login/create-otp', { mobile: form.mobile });
      setStep('otp');
    } catch (err) {
      const msg = err?.response?.data?.message || 'Failed to send OTP. Please try again.';
      showToast(msg);
    } finally {
      setOtpLoading(false);
    }
  };

  const handleVerifyOtp = async () => {
    setVerifyLoading(true);
    try {
      // Step 1: verify OTP
      await axiosInstance.post('/login/verify-otp', {
        mobile: form.mobile,
        otp: otpDigits.join(''),
      });

      // Step 2: save OTP consents; skipPanLookup = no Surepass mobile→PAN lookup from the dashboard
      const { userId, applicationId } = createdApp;
      await axiosInstance.post('/end-user/submit/eligibility-otp', { userId, skipPanLookup: true });

      // Step 3: soft pull + rules on the PAN entered on the form
      // isChanged: true so the backend uses this PAN instead of looking for a Surepass one
      const res = await axiosInstance.post('/end-user/submit/eligibility-pan', {
        userId,
        applicationId,
        pan: form.pan,
        isChanged: true,
      });
      // 'red' = failed eligibility; backend has already moved the application to Rejected
      if (res?.data?.data?.ruleResult === 'red') {
        setStep('form');
        setShowSorry(true);
      } else {
        setStep('eligible');
      }
    } catch (err) {
      const msg = err?.response?.data?.message || 'Verification failed. Please try again.';
      showToast(msg);
    } finally {
      setVerifyLoading(false);
    }
  };

  const [withoutOtpLoading, setWithoutOtpLoading] = useState(false);

  const handleSubmitWithoutOtp = async () => {
    setWithoutOtpLoading(true);
    try {
      await axiosInstance.post('/end-user/submit/send-whatsapp-consent-v2', {
        userId: createdApp.userId,
        applicationId: createdApp.applicationId,
      });
      setStep('app-created');
    } catch (err) {
      const msg = err?.response?.data?.message || 'Failed to send consent. Please try again.';
      showToast(msg);
    } finally {
      setWithoutOtpLoading(false);
    }
  };

  const handleSendLink = () => {
    // API integration point: POST trigger-link endpoint with application details
    setStep('link-sent');
  };

  // ── Reset ──────────────────────────────────────────────────────────

  const resetForm = () => {
    setForm({ mobile: '', name: '', institute: '', course: '', fees: '', studentName: '', pan: '', dob: '' });
    setSameAsStudent(false);
    setInstQuery(''); setInstResults([]); setSelectedInstId(null);
    setCourseQuery(''); setCourseResults([]);
    setError('');
    setSubmitting(false);
    setCreatedApp({ userId: null, applicationId: null });
    setStep('form');
    setOtpDigits(['', '', '', '', '', '']);
    setConsentChecked(false);
    setShowSorry(false);
  };

  return (
    <div style={{ width: '100%', height: '100%', backgroundColor: '#FFF' }}>
        <div className="elig-page">
          <div className="elig-form-wrapper">

            <div className="elig-welcome">
              <p style={{ fontWeight: '500', fontSize: '1.5em', fontFamily: 'Outfit', margin: 0 }}>
                Welcome ! <span style={{ color: '#d32028', fontFamily: 'Outfit' }}>{decode.display_name}</span>
              </p>
              <p style={{ fontSize: '0.9em', fontFamily: 'Outfit', margin: '4px 0 0', color: '#555' }}>
                Fill in the applicant details below to generate an application link.
              </p>
            </div>

            {/* ── Step: Form ── */}
            {step === 'form' && (
              <form onSubmit={handleSubmit}>
                <Box className="fields-container">
                  <h5 className="form-heading">Student Details</h5>
                  <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px 24px', px: 2, pb: 2 }}>

                    <TextField
                      label="Student Name"
                      value={form.studentName}
                      onChange={e => {
                        const val = e.target.value;
                        setForm(p => ({ ...p, studentName: val, ...(sameAsStudent ? { name: val } : {}) }));
                      }}
                      required
                      size="small"
                      InputProps={tfInputProps}
                      InputLabelProps={{ sx: tfLabelSx }}
                      sx={{ ...tfSx, width: '100%', mb: 0 }}
                    />

                    <Box sx={{ gridColumn: '1 / -1' }}>
                      <div className="elig-consent-row" style={{ padding: 0 }}>
                        <input
                          type="checkbox"
                          id="same-as-student"
                          checked={sameAsStudent}
                          onChange={e => {
                            const checked = e.target.checked;
                            setSameAsStudent(checked);
                            setForm(p => ({ ...p, name: checked ? p.studentName : '' }));
                          }}
                          className="elig-consent-check"
                        />
                        <label htmlFor="same-as-student" className="elig-consent-label">
                          Are the applicant and student same?
                        </label>
                      </div>
                    </Box>

                  </Box>
                </Box>

                <Box className="fields-container">
                  <h5 className="form-heading">Applicant Details</h5>
                  <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px 24px', px: 2, pb: 2 }}>

                    <TextField
                      label="Applicant Mobile"
                      value={form.mobile}
                      onChange={e => setForm(p => ({ ...p, mobile: e.target.value.replace(/\D/g, '') }))}
                      type="tel"
                      inputProps={{ inputMode: 'numeric', pattern: '[0-9]{10}', maxLength: 10 }}
                      required
                      size="small"
                      InputProps={tfInputProps}
                      InputLabelProps={{ sx: tfLabelSx }}
                      sx={{ ...tfSx, width: '100%', mb: 0 }}
                    />

                    <TextField
                      label="Applicant Name"
                      value={form.name}
                      onChange={e => !sameAsStudent && setForm(p => ({ ...p, name: e.target.value }))}
                      disabled={sameAsStudent}
                      required
                      size="small"
                      InputProps={tfInputProps}
                      InputLabelProps={{ sx: tfLabelSx }}
                      sx={{ ...tfSx, width: '100%', mb: 0 }}
                    />

                    <TextField
                      label="PAN Number"
                      value={form.pan}
                      onChange={e => setForm(p => ({ ...p, pan: e.target.value.toUpperCase() }))}
                      inputProps={{ maxLength: 10, pattern: '[A-Z]{5}[0-9]{4}[A-Z]', style: { textTransform: 'uppercase', letterSpacing: '0.05em' } }}
                      error={form.pan.length > 0 && !PAN_REGEX.test(form.pan)}
                      helperText={form.pan.length > 0 && !PAN_REGEX.test(form.pan) ? 'Enter a valid PAN, e.g. ABCDE1234F' : ''}
                      required
                      size="small"
                      InputProps={tfInputProps}
                      InputLabelProps={{ sx: tfLabelSx }}
                      sx={{ ...tfSx, width: '100%', mb: 0 }}
                    />

                    <TextField
                      label="Date of Birth"
                      type="date"
                      value={form.dob}
                      onChange={e => setForm(p => ({ ...p, dob: e.target.value }))}
                      required
                      size="small"
                      InputProps={tfInputProps}
                      InputLabelProps={{ shrink: true, sx: tfLabelSx }}
                      sx={{ ...tfSx, width: '100%', mb: 0 }}
                    />

                    {!isInstituteAutofillRole ? (
                      <Box ref={instDropRef} sx={{ position: 'relative' }}>
                        <TextField
                          label="Institute Name"
                          value={instQuery}
                          onChange={e => handleInstSearch(e.target.value)}
                          onFocus={() => instResults.length > 0 && setInstOpen(true)}
                          autoComplete="off"
                          required
                          size="small"
                          InputProps={tfInputProps}
                          InputLabelProps={{ sx: tfLabelSx }}
                          sx={{ ...tfSx, width: '100%', mb: 0 }}
                        />
                        {instOpen && (
                          <ul className="elig-dropdown">
                            {instResults.map(opt => (
                              <li key={opt.id} onMouseDown={() => selectInst(opt)}>{opt.brandName}</li>
                            ))}
                          </ul>
                        )}
                      </Box>
                    ) : (
                      // Institute users can only create leads for their own institute — shown read-only so they know which one
                      <TextField
                        label="Institute Name"
                        value={form.institute}
                        disabled
                        size="small"
                        InputProps={tfInputProps}
                        InputLabelProps={{ sx: tfLabelSx }}
                        sx={{ ...tfSx, width: '100%', mb: 0 }}
                      />
                    )}

                    <Box ref={courseDropRef} sx={{ position: 'relative' }}>
                      <TextField
                        label="Course Name"
                        value={effectiveInstituteId ? courseQuery : form.course}
                        onChange={e => effectiveInstituteId ? handleCourseSearch(e.target.value) : setForm(p => ({ ...p, course: e.target.value }))}
                        onFocus={() => courseResults.length > 0 && setCourseOpen(true)}
                        autoComplete="off"
                        required
                        size="small"
                        InputProps={tfInputProps}
                        InputLabelProps={{ sx: tfLabelSx }}
                        sx={{ ...tfSx, width: '100%', mb: 0 }}
                      />
                      {courseOpen && (
                        <ul className="elig-dropdown">
                          {courseResults.map(opt => (
                            <li key={opt.id} onMouseDown={() => selectCourse(opt)}>{opt.courseName}</li>
                          ))}
                        </ul>
                      )}
                    </Box>

                    <TextField
                      label="Course Fees"
                      type="number"
                      value={form.fees}
                      onChange={e => setForm(p => ({ ...p, fees: e.target.value }))}
                      onWheel={e => e.target.blur()}
                      inputProps={{ min: 0 }}
                      required
                      size="small"
                      InputProps={tfInputProps}
                      InputLabelProps={{ sx: tfLabelSx }}
                      sx={{ ...tfSx, width: '100%', mb: 0 }}
                    />

                  </Box>
                </Box>

                {error && <p className="elig-error">{error}</p>}

                <div className="elig-submit-row">
                  <button type="submit" className="elig-submit-btn" disabled={submitting}>
                    {submitting ? 'Creating...' : 'Proceed'}
                  </button>
                </div>
              </form>
            )}


          </div>
        </div>

      {/* ── Choose action modal ── */}
      {step === 'choose' && (
        <div className="elig-modal-overlay">
          <div className="elig-modal">
            <h2>Application Created</h2>
            <p className="elig-modal-hint">
              Choose how to proceed with <strong>{form.name || 'the applicant'}</strong>
              {form.mobile ? ` (${form.mobile})` : ''}.
            </p>
            <div className="elig-choose-cards">
              <button className="elig-choose-card" onClick={handleSendOtp} disabled={otpLoading}>
                <div className="elig-choose-card-header">
                  <div className="elig-choose-card-icon">
                    <svg width="22" height="22" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                      <path strokeLinecap="round" strokeLinejoin="round" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                  </div>
                  <span className="elig-choose-card-title">Submit with OTP</span>
                </div>
                <span className="elig-choose-card-desc">An OTP will be sent to the applicant's mobile. Submitting it acts as their consent for submitting the application.</span>
              </button>

              <button className="elig-choose-card" onClick={handleSubmitWithoutOtp} disabled={withoutOtpLoading}>
                <div className="elig-choose-card-header">
                  <div className="elig-choose-card-icon">
                    <svg width="22" height="22" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                      <path strokeLinecap="round" strokeLinejoin="round" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                  </div>
                  <span className="elig-choose-card-title">Submit without OTP</span>
                </div>
                <span className="elig-choose-card-desc">Sends a WhatsApp consent request to the applicant's mobile. They can review the terms and confirm to proceed.</span>
              </button>
            </div>
            <button className="elig-close-btn" onClick={() => setStep('form')}>Cancel</button>
          </div>
        </div>
      )}

      {/* ── Link sent success modal ── */}
      {step === 'link-sent' && (
        <div className="elig-modal-overlay">
          <div className="elig-modal" style={{ textAlign: 'center' }}>
            <div className="elig-link-sent-icon">
              <svg width="32" height="32" fill="none" viewBox="0 0 24 24" stroke="#D32028" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1" />
              </svg>
            </div>
            <h2 style={{ marginTop: '12px', marginBottom: '8px' }}>Link Sent Successfully</h2>
            <p className="elig-modal-hint">
              A self-service link has been sent to <strong>{form.mobile}</strong>.
            </p>
            <button className="elig-close-btn" style={{ marginTop: '16px' }} onClick={resetForm}>
              Close
            </button>
          </div>
        </div>
      )}

      {/* ── Eligible success modal ── */}
      {step === 'eligible' && (
        <div className="elig-modal-overlay">
          <div className="elig-modal" style={{ textAlign: 'center' }}>
            <div className="elig-link-sent-icon">
              <svg width="32" height="32" fill="none" viewBox="0 0 24 24" stroke="#D32028" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </div>
            <h2 style={{ marginTop: '12px', marginBottom: '4px' }}>Application Submitted</h2>
            <p className="elig-modal-hint" style={{ marginBottom: '16px' }}>
              Thanks for your submission. Our team will proceed with further steps on this application.
            </p>
            <button className="elig-close-btn" onClick={resetForm}>Close</button>
          </div>
        </div>
      )}

      {/* ── App created success modal ── */}
      {step === 'app-created' && (
        <div className="elig-modal-overlay">
          <div className="elig-modal" style={{ textAlign: 'center' }}>
            <div className="elig-link-sent-icon">
              <svg width="32" height="32" fill="none" viewBox="0 0 24 24" stroke="#D32028" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </div>
            <h2 style={{ marginTop: '12px', marginBottom: '8px' }}>Application Created Successfully</h2>
            <p className="elig-modal-hint">
              The application for <strong>{form.name || form.mobile}</strong> has been created and will be reviewed by our team.
            </p>
            <button className="elig-close-btn" style={{ marginTop: '16px' }} onClick={resetForm}>
              Close
            </button>
          </div>
        </div>
      )}

      {/* ── OTP entry modal ── */}
      {step === 'otp' && (
        <div className="elig-modal-overlay">
          <div className="elig-modal">
            <h2>Verify OTP</h2>
            <p className="elig-modal-hint">
              Enter the OTP sent to <strong>{form.mobile}</strong> to give consent for submitting the application.
            </p>
            <div className="elig-otp-boxes-row">
              {otpDigits.map((digit, i) => (
                <input
                  key={i}
                  ref={el => otpRefs.current[i] = el}
                  type="text"
                  inputMode="numeric"
                  maxLength={1}
                  value={digit}
                  autoFocus={i === 0}
                  onChange={e => handleOtpDigitChange(i, e.target.value)}
                  onKeyDown={e => handleOtpKeyDown(i, e)}
                  onPaste={handleOtpPaste}
                  className="elig-otp-box"
                />
              ))}
            </div>
            <div className="elig-consent-row" style={{ marginBottom: '16px' }}>
              <input
                type="checkbox"
                id="cibil-consent"
                checked={consentChecked}
                onChange={e => setConsentChecked(e.target.checked)}
                className="elig-consent-check"
              />
              <label htmlFor="cibil-consent" className="elig-consent-label">
                By proceeding, you agree to our{' '}
                <a href="https://www.feemonk.com/terms-and-condition" target="_blank" rel="noopener noreferrer" className="elig-consent-link">Terms and Conditions</a>
                {' '}and{' '}
                <a href="https://www.feemonk.com/privacy-policy" target="_blank" rel="noopener noreferrer" className="elig-consent-link">Privacy Policy</a>
                , and authorize feemonk to get a background check and a consumer credit report on me..
              </label>
            </div>
            <button
              type="button"
              className="elig-submit-btn"
              style={{ width: '100%', height: '42px', borderRadius: '6px', marginBottom: '12px' }}
              disabled={!consentChecked || otpDigits.some(d => d === '') || verifyLoading}
              onClick={handleVerifyOtp}
            >
              {verifyLoading ? 'Verifying...' : 'Verify OTP'}
            </button>
            <div className="elig-fallback-row">
              <button type="button" className="elig-fallback-link" onClick={handleSendOtp}>
                Resend OTP
              </button>
              <span className="elig-fallback-sep">·</span>
              <button type="button" className="elig-fallback-link" onClick={() => setStep('choose')}>
                Go Back
              </button>
            </div>
          </div>
        </div>
      )}

      {showSorry && <SorryModal onClose={resetForm} />}

      <Toast
        message={toastMsg}
        visible={toastVisible}
        type={toastType}
        onHide={() => setToastVisible(false)}
      />
    </div>
  );
}
